import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  describeGitError,
  gitConflictsPath,
  gitErrorMessage,
  gitHistoryPath,
  gitSyncKeys,
  invalidateAfterSync,
  readGitError,
  useCancelOperation,
  useFetchRemote,
  useOperation,
  useStartSync,
  useSyncUiStore,
  type GitOperation,
  type SyncResult,
} from "@entities/git-sync";
import { flushPendingSaves, toast, useToastStore } from "@shared/lib";
import type { SyncPrimaryAction } from "./syncUi.model";
import { useSyncView, type SyncView } from "./useSyncView";

// Итог считается «свежим» в течение этого времени: старую синхронизацию,
// найденную при открытии проекта, тостом не поднимаем.
const FRESH_SYNC_MS = 2 * 60_000;

// Запись last_sync после показанного итога приходит с очередным опросом состояния.
const EXPECT_RECORD_MS = 30_000;

const isSyncResult = (
  result: GitOperation["result"] | undefined,
): result is SyncResult =>
  !!result && typeof (result as { outcome?: unknown }).outcome === "string";

/** Как закончился запуск автосинхронизации (для хука `useAutoSync`). */
export type AutoRunResult = {
  operationId: string;
  /** false: сбой операции; отмена и конфликт сбоем не считаются. */
  ok: boolean;
  at: number;
  /** Сколько запусков подряд закончились сбоем, включая этот; успех обнуляет. */
  failures: number;
};

/** Итог попытки запустить автосинхронизацию. */
export type AutoStart = "started" | "joined" | "failed";

export type SyncController = SyncView & {
  /** Операция, за которой следит экран (запущена здесь, в другой вкладке или до перезагрузки). */
  operation: GitOperation | undefined;
  isStarting: boolean;
  isChecking: boolean;
  isCancelling: boolean;
  run: () => Promise<void>;
  /**
   * Запуск от автосинхронизации: те же дописывание правок и операция, но без тостов успеха и без
   * перехода на экран конфликтов; сбой не показывается тостом сам, о нём говорит `autoResult`.
   */
  runAuto: () => Promise<AutoStart>;
  /** Итог последней автозапущенной операции. */
  autoResult: AutoRunResult | null;
  check: () => Promise<void>;
  cancel: () => void;
  /** То, что делает щелчок по основной части элемента. */
  runPrimary: (primary: SyncPrimaryAction) => void;
  openConflicts: () => void;
};

/**
 * Запуск и сопровождение синхронизации: сначала дописывает несохранённые правки
 * редакторов, потом просит сервер, потом следит за операцией и один раз
 * сообщает итог. Опираться на «я сам нажал» нельзя: операцию могут начать в
 * другой вкладке или до перезагрузки страницы, поэтому за ней следим по
 * состоянию проекта.
 */
export const useSyncController = (projectId: string): SyncController => {
  const { t } = useTranslation("gitSync");
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const view = useSyncView(projectId);
  const { status, ui } = view;

  const startSync = useStartSync();
  const { mutateAsync: startSyncAsync } = startSync;
  const fetchRemote = useFetchRemote();
  const cancelOperation = useCancelOperation();

  // Операция, запущенная отсюда: её видно сразу, до того как состояние проекта
  // узнает о ней. Состояние проекта приоритетнее: пока там идёт другая операция,
  // следим за ней.
  const [startedId, setStartedId] = useState<string | null>(null);
  const [isStarting, setIsStarting] = useState(false);
  const trackedId = status?.operation?.id ?? startedId;
  const { data: operation } = useOperation(trackedId);

  const startingRef = useRef(false);
  const startedHere = useRef(new Set<string>());
  // Операции, запущенные автосинхронизацией: успех молчит, экран конфликтов сам не открывается.
  const autoRuns = useRef(new Set<string>());
  const [autoResult, setAutoResult] = useState<AutoRunResult | null>(null);
  const autoFailures = useRef(0);
  const handled = useRef(new Set<string>());
  // Когда итог показан по операции: новую запись last_sync, что появится в
  // состоянии следом, «погасим» без второго тоста.
  const expectSyncRecordAt = useRef(0);
  // Бессрочное уведомление о конфликтах: когда решение принято, оно теряет смысл.
  const conflictToastId = useRef<string | null>(null);

  const openConflicts = useCallback((): void => {
    // Панель, из которой запустили синхронизацию, не должна повиснуть над экраном конфликтов.
    useSyncUiStore.getState().setPopoverOpen(false);
    void navigate(gitConflictsPath(projectId));
  }, [navigate, projectId]);

  const openPopover = useCallback((): void => {
    useSyncUiStore.getState().setPopoverOpen(true);
  }, []);

  // --- итог операции -----------------------------------------------------------

  const announceFinished = useCallback(
    (op: GitOperation, mine: boolean, auto: boolean): void => {
      if (op.status === "failed" && auto) {
        // Автосинхронизация повторит попытку сама; человеку один отчёт на серию сбоев.
        if (autoFailures.current === 1) {
          toast.warning(t("autoSync.toast.failed"), {
            title: t("autoSync.toast.failedTitle"),
            duration: 10_000,
            action: { label: t("toast.details"), onClick: openPopover },
          });
        }
        return;
      }
      if (op.status === "failed") {
        const message = op.error
          ? gitErrorMessage(op.error.code, op.error.detail)
          : t("toast.failedUnknown");
        toast.error(message, {
          title: t("toast.failedTitle"),
          duration: 10_000,
          action: { label: t("toast.details"), onClick: openPopover },
        });
        return;
      }
      if (op.status === "cancelled") {
        toast.info(t("toast.cancelled"));
        return;
      }
      if (!isSyncResult(op.result)) return;
      const result = op.result;

      if (result.outcome === "conflicts") {
        const count = result.conflicts?.files_open ?? 0;
        conflictToastId.current = toast.warning(
          t("toast.conflicts", { count }),
          {
            title: t("toast.conflictsTitle"),
            // Конфликт не исчезает сам: уведомление остаётся, пока его не закроют.
            duration: 0,
            action: { label: t("toast.resolve"), onClick: openConflicts },
          },
        );
        // Только тому, кто сам запустил: чужой экран из-под рук не уводим.
        if (mine) openConflicts();
        return;
      }

      expectSyncRecordAt.current = Date.now();
      // Фоновая синхронизация о себе не кричит: итог виден в состоянии проекта.
      if (auto) return;
      const autoChanges = result.auto.length;
      const base = ((): string => {
        switch (result.outcome) {
          case "up_to_date":
            return t("toast.up_to_date");
          case "pushed":
            return t("toast.pushed", { count: result.pushed_commits });
          case "fast_forwarded":
            return t("toast.fast_forwarded", { count: result.pulled_commits });
          default:
            return t("toast.merged", {
              pulled: result.pulled_commits,
              pushed: result.pushed_commits,
            });
        }
      })();
      const message =
        autoChanges > 0
          ? `${base}. ${t("toast.auto", { count: autoChanges })}`
          : base;
      toast.success(message, {
        title: t("toast.syncedTitle"),
        ...(result.outcome === "up_to_date"
          ? {}
          : {
              action: {
                label: t("toast.openHistory"),
                onClick: () => {
                  void navigate(gitHistoryPath(projectId));
                },
              },
            }),
      });
      if (result.warnings.length > 0) {
        const list = result.warnings
          .map((w) => t(`warning.${w.code}`, { defaultValue: w.code }))
          .join("; ");
        toast.warning(t("toast.warnings", { list }), { duration: 10_000 });
      }
    },
    [navigate, openConflicts, openPopover, projectId, t],
  );

  // Следим за кэшем операций, а не за состоянием компонента: итог надо показать
  // ровно один раз, а перерисовок и повторных чтений операции много.
  useEffect(() => {
    const cache = queryClient.getQueryCache();
    return cache.subscribe((event) => {
      if (event.type !== "updated") return;
      const key = event.query.queryKey as readonly unknown[];
      if (key[0] !== gitSyncKeys.all[0] || key[1] !== "operation") return;
      const op = event.query.state.data as GitOperation | undefined;
      if (!op || op.status === "running") return;
      // Клон и импорт архива живут в своих экранах; чужие проекты не трогаем.
      if (op.kind !== "sync" && op.kind !== "resolve") return;
      if (op.project_id !== projectId) return;
      if (handled.current.has(op.id)) return;
      handled.current.add(op.id);
      const auto = autoRuns.current.has(op.id);
      const failed = op.status === "failed";
      // Счёт неудач ведут и ручные запуски: удачная ручная синхронизация снимает остановку автосинхронизации.
      if (auto || !failed) {
        autoFailures.current = failed ? autoFailures.current + 1 : 0;
        setAutoResult({
          operationId: op.id,
          ok: !failed,
          at: Date.now(),
          failures: autoFailures.current,
        });
      }
      announceFinished(op, startedHere.current.has(op.id) && !auto, auto);
      // Слияние поменяло файлы, версии, подписи и формы.
      invalidateAfterSync(queryClient, projectId);
    });
  }, [announceFinished, projectId, queryClient]);

  const syncState = status ? ui.state : null;
  useEffect(() => {
    if (syncState === null || syncState === "conflicts") return;
    if (conflictToastId.current === null) return;
    useToastStore.getState().dismiss(conflictToastId.current);
    conflictToastId.current = null;
  }, [syncState]);

  // Операция закончилась, пока страница была закрыта (или в другой вкладке и без
  // нашего наблюдения): сервер помнит итог в last_sync, показываем его один раз.
  const lastSync = status?.last_sync ?? null;
  const isOperationActive = !!status?.operation;
  useEffect(() => {
    if (!lastSync || isOperationActive) return;
    if (startingRef.current) return;
    const store = useSyncUiStore.getState();
    if (!store.claimSyncToast(projectId, lastSync.id)) return;
    // Конфликт уже показан состоянием проекта, баннером и тостом операции.
    if (lastSync.outcome === "conflicts") return;
    // Операцию запустили здесь, а её итог ещё не показан: опрос состояния
    // обогнал опрос операции, подробный тост даст она.
    for (const id of startedHere.current) {
      if (!handled.current.has(id)) return;
    }
    if (Date.now() - expectSyncRecordAt.current < EXPECT_RECORD_MS) {
      expectSyncRecordAt.current = 0;
      return;
    }
    if (Date.now() - Date.parse(lastSync.at) > FRESH_SYNC_MS) return;
    toast.success(
      lastSync.outcome === "up_to_date"
        ? t("toast.up_to_date")
        : t("toast.merged", {
            pulled: lastSync.pulled_commits,
            pushed: lastSync.pushed_commits,
          }),
      { title: t("toast.syncedTitle") },
    );
  }, [isOperationActive, lastSync, projectId, t]);

  // --- действия ----------------------------------------------------------------

  // Автосинхронизация не смогла даже начать (правки не сохранились, сервер отказал в запуске): тоже неудача.
  const noteAutoStartFailure = useCallback((): void => {
    autoFailures.current += 1;
    if (autoFailures.current === 1) {
      toast.warning(t("autoSync.toast.failed"), {
        title: t("autoSync.toast.failedTitle"),
        duration: 10_000,
        action: { label: t("toast.details"), onClick: openPopover },
      });
    }
    setAutoResult({
      operationId: `start-failed-${String(autoFailures.current)}-${String(Date.now())}`,
      ok: false,
      at: Date.now(),
      failures: autoFailures.current,
    });
  }, [openPopover, t]);

  // Общий запуск: ручной (`auto = false`) говорит о сбоях тостами, автоматический молчит и возвращает итог.
  const start = useCallback(
    async (auto: boolean): Promise<AutoStart> => {
      if (startingRef.current) return "joined";
      startingRef.current = true;
      setIsStarting(true);
      const track = (id: string): void => {
        startedHere.current.add(id);
        if (auto) autoRuns.current.add(id);
        setStartedId(id);
      };
      try {
        // Правки в редакторах дописываются с задержкой; снимок без них вышел бы
        // неполным, а сами они потом легли бы поверх слитых файлов.
        const flushed = await flushPendingSaves();
        if (flushed.failed > 0) {
          if (auto) {
            noteAutoStartFailure();
          } else {
            toast.error(t("toast.saveFailed"), {
              title: t("toast.saveFailedTitle"),
              duration: 10_000,
            });
          }
          return "failed";
        }
        const { operation_id: operationId } = await startSyncAsync(projectId);
        track(operationId);
        return "started";
      } catch (error) {
        const body = readGitError(error);
        if (body?.code === "sync_busy" && body.operation_id) {
          // Уже идёт: подключаемся к ней, как будто сами запустили.
          track(body.operation_id);
          return "joined";
        }
        if (auto) {
          noteAutoStartFailure();
        } else {
          toast.error(describeGitError(error, t("toast.failedUnknown")), {
            title: t("toast.startFailedTitle"),
          });
        }
        return "failed";
      } finally {
        startingRef.current = false;
        setIsStarting(false);
      }
    },
    [noteAutoStartFailure, projectId, startSyncAsync, t],
  );

  const run = useCallback(async (): Promise<void> => {
    await start(false);
  }, [start]);

  const runAuto = useCallback((): Promise<AutoStart> => start(true), [start]);

  const check = useCallback(async (): Promise<void> => {
    try {
      const fresh = await fetchRemote.mutateAsync(projectId);
      if (fresh.behind > 0) {
        toast.info(t("toast.checkFound", { count: fresh.behind }));
      } else {
        toast.success(t("toast.checkNone"));
      }
    } catch (error) {
      toast.error(describeGitError(error, t("toast.failedUnknown")), {
        title: t("toast.checkFailedTitle"),
      });
    }
  }, [fetchRemote, projectId, t]);

  const cancel = useCallback((): void => {
    const id = status?.operation?.id ?? trackedId;
    if (!id) return;
    cancelOperation.mutate(id, {
      onError: (error) => {
        toast.error(describeGitError(error, t("toast.failedUnknown")));
      },
    });
  }, [cancelOperation, status?.operation?.id, t, trackedId]);

  const runPrimary = useCallback(
    (primary: SyncPrimaryAction): void => {
      const store = useSyncUiStore.getState();
      switch (primary) {
        case "connect":
          store.openConnectDialog("connect");
          return;
        case "fix_access":
          store.openConnectDialog("fix_access");
          return;
        case "sync":
          void run();
          return;
        case "retry":
          void check();
          return;
        case "resolve":
          openConflicts();
          return;
        default:
          openPopover();
      }
    },
    [check, openConflicts, openPopover, run],
  );

  // --- команды из палитры и сочетания клавиш -------------------------------------

  const command = useSyncUiStore((state) => state.command);
  useEffect(() => {
    if (!command) return;
    useSyncUiStore.getState().consumeCommand(command.id);
    if (!ui.visible) return;
    // Команда пришла снаружи (палитра, клавиши), а выполнение меняет состояние
    // экрана: откладываем его из тела эффекта.
    queueMicrotask(() => {
      if (command.kind === "run") {
        if (ui.canSync) void run();
        else openPopover();
        return;
      }
      if (command.kind === "check") {
        if (ui.canCheck) void check();
        else openPopover();
        return;
      }
      runPrimary(ui.primary);
    });
  }, [check, command, openPopover, run, runPrimary, ui]);

  return {
    ...view,
    operation,
    isStarting,
    isChecking: fetchRemote.isPending,
    isCancelling: cancelOperation.isPending,
    run,
    runAuto,
    autoResult,
    check,
    cancel,
    runPrimary,
    openConflicts,
  };
};
